import React, { useEffect, useRef, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import customClick from "../../images/catalogues/_xmarkCustom.svg";
import chevronRight from "../../images/catalogues/chevron-right.svg";
import Spinner from "../Spinner/Spinner";
import Cart from "./Cart";
import Categories from "./Categories";
import CustomOverlay from "./CustomOverlay";
import Overlay from "./Overlay";
import ProductCard from "./ProductCard";
import xmarkAC from "../../images/catalogues/_xmarkAC.svg";
import styles from "./Products.module.css";

function Products() {
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState();
  const [cate, setCate] = useState([]);
  const [spin, setSpin] = useState(true);
  const [expand, setExpand] = useState(false);
  const [singleProduct, setSingleProduct] = useState();
  const [open, setOpen] = useState(true);
  const [cartOpen, setCartOpen] = useState(true);
  const [customOpen, setCustomOpen] = useState(false);
  const [selectedproducts, setSelectedproducts] = useState(
    location.state?.selectedproducts || []
  );
  const [customproducts, setCustomproducts] = useState(
    location.state?.selectedCustomproducts || []
  );
  const [selectedCategory, setSelectedCategory] = useState();
  const [addimage, setAddimage] = useState({});
  const [selectedProductsId, setSelectedProductsId] = useState([]);
  const [value, setValue] = useState("");
  const [id, setId] = useState(null);
  const testRef = useRef(null);

  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [visibleCategoryIndex, setVisibleCategoryIndex] = useState(0);
  const [categoryFullyLoadedMap, setCategoryFullyLoadedMap] = useState({});
  const [currentSubcategoryIndex, setCurrentSubcategoryIndex] = useState(-1); // -1 means "no subcategory" (subCategoryId=null)

  const [loadingMore, setLoadingMore] = useState(false);
  const [allCategoriesLoaded, setAllCategoriesLoaded] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const PRODUCTS_PER_PAGE = 20;
  const loadingRef = useRef(false);
  const scheduledLoadRef = useRef(false);
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const currentPageRef = useRef(1);
  const hasNextPageRef = useRef(true);
  const currentCategoryIndexRef = useRef(0);
  const currentSubcategoryIndexRef = useRef(-1); // -1 means "no subcategory" (subCategoryId=null)
  const categoryLoadingRef = useRef(new Set());
  const initializedRef = useRef(false);
  const scrollTickingRef = useRef(false);

  const getCategories = async () => {
    const data = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/category/get`
    );
    const json = await data.json();
    setCategories(json.allcategory);
  };

  useEffect(() => {
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;
    getCategories();
  }, []);

  const getSubcategoriesForCategory = (category) => {
    if (!category || !Array.isArray(category.subCategories)) return [];
    return [...category.subCategories]
      .filter((s) => s.isActive !== false)
      .sort((a, b) => {
        const sa = typeof a.sortId === "number" ? a.sortId : 0;
        const sb = typeof b.sortId === "number" ? b.sortId : 0;
        return sa - sb;
      });
  };

  const getData = async () => {
    if (!categories || categories.length === 0) return;

    const firstCategoryId = categories[0]._id;

    if (categoryLoadingRef.current.has(firstCategoryId)) {
      return;
    }

    categoryLoadingRef.current.add(firstCategoryId);

    currentSubcategoryIndexRef.current = -1;
    setCurrentSubcategoryIndex(-1);

    try {
      await loadCategoryProducts(firstCategoryId, null, 1, false);
    } catch (error) {
      categoryLoadingRef.current.delete(firstCategoryId);
    }
  };

  const loadCategoryProducts = async (
    categoryId,
    subCategoryId,
    page,
    append
  ) => {
    try {
      if (append) {
        setLoadingMore(true);
      }

      let url = `${process.env.REACT_APP_BACKEND_URL}/productList?categoryId=${categoryId}&page=${page}&limit=${PRODUCTS_PER_PAGE}`;
      if (subCategoryId === null) {
        url += `&subCategoryId=null`;
      } else if (subCategoryId) {
        url += `&subCategoryId=${subCategoryId}`;
      }

      const response = await fetch(url);
      const json = await response.json();

      if (append) {
        setProducts((prev) => {
          const existingIds = new Set(prev.map((p) => p._id));
          const newProducts = (json.products || []).filter(
            (p) => !existingIds.has(p._id)
          );
          return [...prev, ...newProducts];
        });
      } else {
        setProducts(json.products || []);
      }

      if (json.pagination) {
        currentPageRef.current = json.pagination.currentPage;
        hasNextPageRef.current = json.pagination.hasNextPage;
      }

      setSpin(false);
      setLoadingMore(false);
      setInitialLoadComplete(true);
    } catch (error) {
      console.error("Error loading category products:", error);
      setSpin(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (categories && categories.length > 0 && !initialLoadComplete) {
      getData();
    }
  }, [categories, initialLoadComplete]);

  const isSentinelVisible = () => {
    if (!sentinelRef.current) return false;
    const rect = sentinelRef.current.getBoundingClientRect();
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight;
    return rect.top <= windowHeight + 400;
  };

  const scheduleLoad = () => {
    if (scheduledLoadRef.current) {
      return;
    }
    scheduledLoadRef.current = true;
    setTimeout(() => {
      scheduledLoadRef.current = false;
      loadMoreProducts();
    }, 100);
  };

  const loadMoreProducts = async () => {
    if (loadingRef.current || allCategoriesLoaded || !categories) {
      return;
    }

    const currentCategory = categories[currentCategoryIndexRef.current];
    if (!currentCategory) {
      setAllCategoriesLoaded(true);
      loadingRef.current = false;
      return;
    }

    loadingRef.current = true;

    try {
      if (hasNextPageRef.current) {
        const nextPage = currentPageRef.current + 1;
        const currentSubcategoryId =
          currentSubcategoryIndexRef.current === -1
            ? null
            : getSubcategoriesForCategory(currentCategory)[
                currentSubcategoryIndexRef.current
              ]?._id || null;

        await loadCategoryProducts(
          currentCategory._id,
          currentSubcategoryId,
          nextPage,
          true
        );

        if (
          !hasNextPageRef.current &&
          isSentinelVisible() &&
          !allCategoriesLoaded
        ) {
          await moveToNextStream();
        }

        loadingRef.current = false;

        if (
          hasNextPageRef.current &&
          isSentinelVisible() &&
          !allCategoriesLoaded
        ) {
          scheduleLoad();
        }
      } else {
        await moveToNextStream();
        loadingRef.current = false;

        // If next stream has pages and sentinel is visible, schedule another load
        if (
          hasNextPageRef.current &&
          isSentinelVisible() &&
          !allCategoriesLoaded
        ) {
          scheduleLoad();
        }
      }
    } catch (error) {
      console.error("Error in loadMoreProducts:", error);
      loadingRef.current = false;
    }
  };

  const moveToNextStream = async () => {
    if (!categories || categories.length === 0) {
      setAllCategoriesLoaded(true);
      return;
    }

    const currentCategory = categories[currentCategoryIndexRef.current];
    if (!currentCategory) {
      setAllCategoriesLoaded(true);
      return;
    }

    const subCategories = getSubcategoriesForCategory(currentCategory);

    if (currentSubcategoryIndexRef.current === -1) {
      if (subCategories.length > 0) {
        currentSubcategoryIndexRef.current = 0;
        setCurrentSubcategoryIndex(0);
        currentPageRef.current = 1;
        hasNextPageRef.current = true;
        await loadCategoryProducts(
          currentCategory._id,
          subCategories[0]._id,
          1,
          true
        );
        return;
      } else {
        const nextCategoryIndex = currentCategoryIndexRef.current + 1;
        if (nextCategoryIndex >= categories.length) {
          setAllCategoriesLoaded(true);
          return;
        }

        currentCategoryIndexRef.current = nextCategoryIndex;
        setCurrentCategoryIndex(nextCategoryIndex);
        currentSubcategoryIndexRef.current = -1;
        setCurrentSubcategoryIndex(-1);
        currentPageRef.current = 1;
        hasNextPageRef.current = true;

        const nextCategory = categories[nextCategoryIndex];
        await loadCategoryProducts(nextCategory._id, null, 1, true);

        setTimeout(() => {
          loadCategoriesUpward(nextCategoryIndex);
        }, 200);
        return;
      }
    }

    const nextSubIndex = currentSubcategoryIndexRef.current + 1;
    if (nextSubIndex < subCategories.length) {
      currentSubcategoryIndexRef.current = nextSubIndex;
      setCurrentSubcategoryIndex(nextSubIndex);
      currentPageRef.current = 1;
      hasNextPageRef.current = true;
      await loadCategoryProducts(
        currentCategory._id,
        subCategories[nextSubIndex]._id,
        1,
        true
      );
      return;
    }

    const nextCategoryIndex = currentCategoryIndexRef.current + 1;
    if (nextCategoryIndex >= categories.length) {
      setAllCategoriesLoaded(true);
      return;
    }

    currentCategoryIndexRef.current = nextCategoryIndex;
    setCurrentCategoryIndex(nextCategoryIndex);
    currentSubcategoryIndexRef.current = -1;
    setCurrentSubcategoryIndex(-1);
    currentPageRef.current = 1;
    hasNextPageRef.current = true;

    const nextCategory = categories[nextCategoryIndex];
    await loadCategoryProducts(nextCategory._id, null, 1, true);

    setTimeout(() => {
      loadCategoriesUpward(nextCategoryIndex);
    }, 200);
  };

  useEffect(() => {
    if (!initialLoadComplete || allCategoriesLoaded || !sentinelRef.current) {
      return;
    }
    const options = {
      root: null,
      rootMargin: "400px",
      threshold: 0.1,
    };

    const handleIntersect = (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && !loadingRef.current && !allCategoriesLoaded) {
        loadMoreProducts();
      }
    };

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver(handleIntersect, options);
    observer.observe(sentinelRef.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [initialLoadComplete, allCategoriesLoaded, currentCategoryIndex]);

  const deleteProduct = async (index) => {
    let temp = [...selectedproducts];
    temp = temp.filter((item, i) => {
      if (i !== index) {
        return item;
      }
    });
    setSelectedproducts(temp);
  };

  const deleteCustomProduct = async (index) => {
    let temp = [...customproducts];
    temp = temp.filter((item, i) => {
      if (i !== index) {
        return item;
      }
    });
    setCustomproducts(temp);
  };

  const swagHandler = () => {
    setCartOpen(true);
    setExpand(!expand);
  };

  const loadSubcategoriesForCategory = async (categoryIndex) => {
    if (!categories || categoryIndex >= categories.length) return;

    const category = categories[categoryIndex];
    const categoryId = category._id;
    const subCategories = getSubcategoriesForCategory(category);

    if (subCategories.length === 0) return;

    const existingProductSubcategoryIds = new Set(
      products
        .filter((p) => p.categoryId === categoryId && p.subCategoryId)
        .map((p) => p.subCategoryId)
    );

    for (const sub of subCategories) {
      if (existingProductSubcategoryIds.has(sub._id)) {
        continue;
      }

      try {
        const subFirstPageUrl = `${process.env.REACT_APP_BACKEND_URL}/productList?categoryId=${categoryId}&subCategoryId=${sub._id}&page=1&limit=${PRODUCTS_PER_PAGE}`;
        const subFirstRes = await fetch(subFirstPageUrl);
        const subFirstJson = await subFirstRes.json();

        const subTotalPages = subFirstJson.pagination?.totalPages || 1;
        const subAllProducts = [...(subFirstJson.products || [])];

        if (subTotalPages > 1) {
          const subPagePromises = [];
          for (let page = 2; page <= subTotalPages; page++) {
            const url = `${process.env.REACT_APP_BACKEND_URL}/productList?categoryId=${categoryId}&subCategoryId=${sub._id}&page=${page}&limit=${PRODUCTS_PER_PAGE}`;
            subPagePromises.push(
              fetch(url)
                .then((res) => res.json())
                .then((json) => json.products || [])
            );
          }

          const subAllPagesResults = await Promise.all(subPagePromises);
          subAllPagesResults.forEach((pageProducts) => {
            subAllProducts.push(...pageProducts);
          });
        }

        setProducts((prev) => {
          const existingIds = new Set(prev.map((p) => p._id));
          const newProducts = subAllProducts.filter(
            (p) => !existingIds.has(p._id)
          );
          return [...prev, ...newProducts];
        });
      } catch (e) {
        console.error(
          `Error loading subcategory ${sub.name} for ${category.name}:`,
          e
        );
      }
    }
  };

  const loadCategoriesUpward = async (targetIndex) => {
    const categoriesToLoad = [];

    for (let i = targetIndex - 1; i >= 0; i--) {
      const catId = categories[i]._id;
      const isFullyLoaded = categoryFullyLoadedMap[catId];
      const isInLoadingRef = categoryLoadingRef.current.has(catId);

      if (!isFullyLoaded && !isInLoadingRef) {
        categoriesToLoad.push(i);
      }
    }

    if (categoriesToLoad.length > 0) {
      try {
        for (const idx of categoriesToLoad) {
          await loadFirstPageForIndex(idx);
        }
      } catch (error) {
        console.error(`Error in background loading:`, error);
      }
    }

    try {
      for (let i = targetIndex - 1; i >= 0; i--) {
        await loadSubcategoriesForCategory(i);
      }
    } catch (error) {
      console.error(`Error loading subcategories upward:`, error);
    }
  };

  const loadFirstPageForIndex = async (categoryIndex) => {
    if (!categories || categoryIndex >= categories.length) return;

    const categoryId = categories[categoryIndex]._id;
    const categoryName = categories[categoryIndex].name;

    if (categoryFullyLoadedMap[categoryId]) {
      return;
    }

    if (categoryLoadingRef.current.has(categoryId)) {
      return;
    }

    categoryLoadingRef.current.add(categoryId);

    try {
      const firstPageUrl = `${process.env.REACT_APP_BACKEND_URL}/productList?categoryId=${categoryId}&subCategoryId=null&page=1&limit=${PRODUCTS_PER_PAGE}`;
      const firstRes = await fetch(firstPageUrl);
      const firstJson = await firstRes.json();

      const totalPages = firstJson.pagination?.totalPages || 1;
      const allProducts = [...(firstJson.products || [])];

      if (totalPages > 1) {
        const pagePromises = [];
        for (let page = 2; page <= totalPages; page++) {
          const url = `${process.env.REACT_APP_BACKEND_URL}/productList?categoryId=${categoryId}&subCategoryId=null&page=${page}&limit=${PRODUCTS_PER_PAGE}`;
          pagePromises.push(
            fetch(url)
              .then((res) => res.json())
              .then((json) => json.products || [])
          );
        }

        const allPagesResults = await Promise.all(pagePromises);

        allPagesResults.forEach((pageProducts) => {
          allProducts.push(...pageProducts);
        });
      }

      const category = categories[categoryIndex];
      const subCategories = getSubcategoriesForCategory(category);

      for (const sub of subCategories) {
        const subFirstPageUrl = `${process.env.REACT_APP_BACKEND_URL}/productList?categoryId=${categoryId}&subCategoryId=${sub._id}&page=1&limit=${PRODUCTS_PER_PAGE}`;
        const subFirstRes = await fetch(subFirstPageUrl);
        const subFirstJson = await subFirstRes.json();

        const subTotalPages = subFirstJson.pagination?.totalPages || 1;
        const subAllProducts = [...(subFirstJson.products || [])];

        if (subTotalPages > 1) {
          const subPagePromises = [];
          for (let page = 2; page <= subTotalPages; page++) {
            const url = `${process.env.REACT_APP_BACKEND_URL}/productList?categoryId=${categoryId}&subCategoryId=${sub._id}&page=${page}&limit=${PRODUCTS_PER_PAGE}`;
            subPagePromises.push(
              fetch(url)
                .then((res) => res.json())
                .then((json) => json.products || [])
            );
          }

          const subAllPagesResults = await Promise.all(subPagePromises);
          subAllPagesResults.forEach((pageProducts) => {
            subAllProducts.push(...pageProducts);
          });
        }

        allProducts.push(...subAllProducts);
      }

      setProducts((prev) => {
        const existingIds = new Set(prev.map((p) => p._id));
        const newProducts = allProducts.filter((p) => !existingIds.has(p._id));
        return [...prev, ...newProducts];
      });

      setCategoryFullyLoadedMap((prev) => ({
        ...prev,
        [categoryId]: true,
      }));
    } catch (e) {
      console.error(`Error loading ${categories[categoryIndex].name}:`, e);
      categoryLoadingRef.current.delete(categoryId);
    }
  };

  const prefetchUnloadedCategoriesAbove = async (targetIndex) => {
    if (!categories || targetIndex <= 0) return;

    const categoriesToPrefetch = [];
    for (let i = 0; i < targetIndex; i++) {
      const catId = categories[i]._id;
      if (
        !categoryFullyLoadedMap[catId] &&
        !categoryLoadingRef.current.has(catId)
      ) {
        categoriesToPrefetch.push(i);
      }
    }

    if (categoriesToPrefetch.length === 0) {
      return;
    }

    const promises = categoriesToPrefetch.map((idx) =>
      loadFirstPageForIndex(idx)
    );
    await Promise.all(promises);
  };

  const scrollToCategory = async (categoryName) => {
    const categoryIndex = categories.findIndex((c) => c.name === categoryName);

    if (categoryIndex === -1) {
      return;
    }

    const categoryId = categories[categoryIndex]._id;
    const oldCategoryIndex = currentCategoryIndex;

    if (categoryIndex > currentCategoryIndex) {
      currentCategoryIndexRef.current = categoryIndex;
      setCurrentCategoryIndex(categoryIndex);
      currentSubcategoryIndexRef.current = -1;
      setCurrentSubcategoryIndex(-1);
    }

    try {
      if (
        !categoryFullyLoadedMap[categoryId] &&
        !categoryLoadingRef.current.has(categoryId)
      ) {
        setLoadingMore(true);
        await loadFirstPageForIndex(categoryIndex);
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
        await loadSubcategoriesForCategory(categoryIndex);
        setLoadingMore(false);
      }
    } catch (error) {
      setLoadingMore(false);
    }

    setTimeout(() => {
      loadCategoriesUpward(categoryIndex);
    }, 100);

    let needsDelay = false;
    if (categoryIndex > oldCategoryIndex && categories && categories.length) {
      for (let i = oldCategoryIndex + 1; i <= categoryIndex; i++) {
        const betweenCategory = categories[i];
        if (!betweenCategory) continue;
        const betweenId = betweenCategory._id;
        if (!categoryFullyLoadedMap[betweenId]) {
          needsDelay = true;
          break;
        }
      }
    }

    if (needsDelay) {
      await new Promise((resolve) => setTimeout(resolve, 1800));
    }

    const element = document.getElementById(categoryName);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setVisibleCategoryIndex(categoryIndex);
    }
  };

  const productsByCategory = useMemo(() => {
    if (!categories || !products) return {};

    const categorized = {};
    categories.forEach((cat) => {
      categorized[cat._id] = products.filter(
        (p) => p.categoryId === cat._id && p.available
      );
    });

    return categorized;
  }, [products, categories]);

  useEffect(() => {
    if (!categories || !categories.length) return;
    const current = categories[visibleCategoryIndex];
    if (current && current.name) {
      setValue(current.name);
    }
  }, [visibleCategoryIndex, categories]);

  useEffect(() => {
    if (!categories || !categories.length) return;

    const handleScroll = () => {
      if (scrollTickingRef.current) return;
      scrollTickingRef.current = true;

      window.requestAnimationFrame(() => {
        scrollTickingRef.current = false;

        const scrollY =
          window.scrollY ||
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          0;
        const viewportHeight =
          window.innerHeight || document.documentElement.clientHeight || 0;
        const docHeight =
          document.documentElement.scrollHeight ||
          document.body.scrollHeight ||
          0;
        const nearBottomThreshold = 200;

        if (
          docHeight - (scrollY + viewportHeight) <= nearBottomThreshold &&
          categories.length > 0
        ) {
          const lastIndex = categories.length - 1;
          if (lastIndex !== visibleCategoryIndex) {
            setVisibleCategoryIndex(lastIndex);
          }
          return;
        }

        let bestIndex = visibleCategoryIndex;
        let bestDistance = Infinity;
        const markerY = 140;
        let coveringIndex = -1;

        categories.forEach((cat, index) => {
          const el = document.getElementById(cat.name);
          if (!el) return;
          const rect = el.getBoundingClientRect();
          const top = rect.top;
          const bottom = rect.bottom;

          if (top <= markerY && bottom >= markerY) {
            coveringIndex = index;
          }

          const distance = Math.abs(top - markerY);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
          }
        });

        const nextIndex = coveringIndex !== -1 ? coveringIndex : bestIndex;

        if (nextIndex !== visibleCategoryIndex) {
          setVisibleCategoryIndex(nextIndex);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [categories, visibleCategoryIndex]);

  document.title = "Hustlemad | Products";
  return (
    <>
      {spin ? (
        <Spinner />
      ) : (
        <div className={styles.mpage}>
          {singleProduct && (
            <Overlay
              product={singleProduct}
              open={open}
              setOpen={setOpen}
              selectedproducts={selectedproducts}
              setSelectedproducts={setSelectedproducts}
            />
          )}
          {customOpen && (
            <CustomOverlay
              setCustomOpen={setCustomOpen}
              uniqueCategories={categories}
              customproducts={customproducts}
              setCustomproducts={setCustomproducts}
              selectedCategory={selectedCategory}
            />
          )}
          <Categories
            onCategoryClick={scrollToCategory}
            categories={categories}
            currentCategory={value}
          />
          <div className={styles.cardsSection} id="card-section" ref={testRef}>
            <div className={styles.cardsdisplay}>
              {categories.map((u, index) => {
                const categoryProducts = productsByCategory[u._id] || [];

                const isCategoryReached = index <= currentCategoryIndex;
                const showLoadingForCategory =
                  index === currentCategoryIndex && loadingMore;

                return (
                  <div key={u._id} className={styles.crds} id={u.name}>
                    <p className={styles.swag}>{u.name}</p>
                    <h1 className={styles.heading}>{u.description}</h1>
                    {u.subDescription?.length != 0 ? (
                      <div className={styles.subheading}>
                        {u.subDescription}
                      </div>
                    ) : null}
                    <div className={styles.pRows}>
                      {isCategoryReached && (
                        <>
                          {categoryProducts
                            .filter(
                              (p) =>
                                !p.subCategoryId ||
                                p.subCategoryId === "null" ||
                                p.subCategoryId === null
                            )
                            .map((p) => (
                              <ProductCard
                                key={p._id}
                                product={p}
                                selectedproducts={selectedproducts}
                                setSelectedproducts={setSelectedproducts}
                                setSingleProduct={setSingleProduct}
                                setOpen={setOpen}
                              />
                            ))}

                          {getSubcategoriesForCategory(u).map((sub) => {
                            const subProducts = categoryProducts.filter(
                              (p) => p.subCategoryId === sub._id
                            );

                            if (!subProducts.length) {
                              return null;
                            }

                            return (
                              <React.Fragment key={sub._id}>
                                <div className={styles.subcategoryLabel}>
                                  <span className={styles.categoryNameInLabel}>
                                    {u.name}
                                  </span>
                                  <img
                                    src={chevronRight}
                                    alt=""
                                    className={styles.subcategoryArrow}
                                  />
                                  <span
                                    className={styles.subcategoryNameInLabel}
                                  >
                                    {sub.name}
                                  </span>
                                </div>
                                {subProducts.map((p) => (
                                  <ProductCard
                                    key={p._id}
                                    product={p}
                                    selectedproducts={selectedproducts}
                                    setSelectedproducts={setSelectedproducts}
                                    setSingleProduct={setSingleProduct}
                                    setOpen={setOpen}
                                  />
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </>
                      )}

                      {!isCategoryReached && (
                        <div className={styles.categoryNotLoaded}>
                          {[1, 2, 3].map((placeholder) => (
                            <div
                              key={placeholder}
                              className={styles.skeletonCard}
                            >
                              <div className={styles.skeletonImage} />
                              <div className={styles.skeletonBrand} />
                              <div className={styles.skeletonBody}>
                                <div className={styles.skeletonLineShort} />
                                <div className={styles.skeletonLineLong} />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {isCategoryReached &&
                        customproducts.map((c, i) => {
                          if (customproducts[i].category === u.name) {
                            return (
                              <div className={styles.pCardsTBD}>
                                <img
                                  className={styles.TBD}
                                  src={u.someField}
                                  alt=""
                                />
                                <div className={styles.pcustombranding}>
                                  <p>Custom {customproducts[i].category}</p>
                                </div>
                                <div className={styles.pCardsbodyTBD}>
                                  <div className={styles.lTBD}>
                                    <h5>{customproducts[i].swagName}</h5>
                                    <p>₹ TBD</p>
                                  </div>
                                  <div className={styles.rTBD}>
                                    <img src={xmarkAC} alt="" />
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        })}
                      {isCategoryReached && u.custom && (
                        <div
                          className={styles.pCardsCustom}
                          onClick={() => (
                            setCustomOpen(true), setSelectedCategory(u.name)
                          )}
                        >
                          <div className={styles.upperCustom}>
                            <h3>Looking for something else?</h3>
                            <p>
                              If only we could read minds! :D You’ll have to
                              tell us.
                            </p>
                          </div>
                          <div className={styles.pCardsbodyCustom}>
                            <img src={customClick} alt="" />
                          </div>
                        </div>
                      )}
                    </div>

                    {index === currentCategoryIndex &&
                      !allCategoriesLoaded &&
                      !spin && (
                        <div
                          ref={sentinelRef}
                          className={styles.loadingSentinel}
                        >
                          {loadingMore && (
                            <div className={styles.loadingIndicator}>
                              <div className={styles.loadingSpinner}></div>
                              <p>Loading more from {u.name}...</p>
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </div>

          {expand ? (
            <div className={styles.cartpopup}>
              <Cart
                deleteProduct={deleteProduct}
                deleteCustomProduct={deleteCustomProduct}
                selectedproducts={selectedproducts}
                disabled={false}
                cartOpen={expand}
                setCartOpen={setExpand}
                customproducts={customproducts}
                quantitycatalogue={location.state?.quantity || "10"}
                uniqueCategories={categories}
                setSelectedproducts={setSelectedproducts}
              />
            </div>
          ) : null}
          <div className={styles.cartsystem}>
            <Cart
              deleteProduct={deleteProduct}
              deleteCustomProduct={deleteCustomProduct}
              selectedproducts={selectedproducts}
              disabled={false}
              cartOpen={true}
              setCartOpen={setExpand}
              customproducts={customproducts}
              quantitycatalogue={location.state?.quantity || "10"}
              uniqueCategories={categories}
              setSelectedproducts={setSelectedproducts}

            />
          </div>
          <div className={styles.view_desktop} onClick={swagHandler}>
            <button className={styles.bttnnn}>
              Expand Swag Box [{selectedproducts.length + customproducts.length}
              ]
            </button>
          </div>
          <div className={styles.view} onClick={swagHandler}>
            <button className={styles.bttnn}>
              Expand Swag Box [{selectedproducts.length + customproducts.length}
              ]
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default Products;
